import { c as _c } from "react/compiler-runtime";
import { isVaultDataDecrypted, type VaultValue } from '@onefootprint/core';
import type { DataAttributeKind, DataIdentifier, EntityAttribute } from '@onefootprint/request-types/dashboard';
import type { Transforms } from '@onefootprint/types/src/data/entity';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { forgetDecryptedFields } from '@/entity/hooks/use-decrypt-controls';
import type { EntityVault } from '@/types/vault';
export type VaultType = {
  vault: EntityVault;
  transforms: Partial<Record<DataIdentifier, Transforms>>;
  dataKinds: Partial<Record<DataIdentifier, DataAttributeKind>>;
};
const getQueryKey = (entityId?: string, seqno?: number) => ['entity', entityId, seqno, 'vault'];

// DIs whose input components apply a Cleave mask (e.g. SSN/TIN dashes). The local cache should
// hold raw digits to match BE storage; otherwise renderers like ssnFormatter (which expects
// exactly 9 chars) silently break on post-edit values.
const MASKED_DIS: DataIdentifier[] = ['id.ssn9', 'id.ssn4', 'id.itin', 'id.us_tax_id', 'business.tin'];
const stripMaskDelimiters = (di: DataIdentifier, value: VaultValue): VaultValue => {
  if (typeof value !== 'string' || !MASKED_DIS.includes(di)) return value;
  return value.replace(/-/g, '');
};

// id.us_tax_id is the BE's umbrella DI; it auto-detects SSN vs ITIN from the first digit and
// derives the sibling on write. Mirror those derivations into the local cache so all rendered
// rows reflect the new value immediately after edit/decrypt, without waiting for a refetch.
// Unconditional by design: on edit-submit, entity.data is still the pre-edit snapshot, so we
// can't gate on it — the BE has just derived the new sibling and we need the optimistic write
// to land before invalidateQueries refetches. Phantom writes for entities truly lacking a
// sibling are harmless because downstream consumers filter by entity.data, not the vault cache.
const deriveSiblingDis = (di: DataIdentifier, value: VaultValue, target: Partial<Record<DataIdentifier, VaultValue>>): void => {
  if (typeof value !== 'string' || !value) return;
  if (di === 'id.ssn9') {
    target['id.ssn4'] = value.slice(-4);
    target['id.us_tax_id'] = value;
  } else if (di === 'id.itin') {
    target['id.us_tax_id'] = value;
  } else if (di === 'id.us_tax_id') {
    if (value.startsWith('9')) {
      target['id.itin'] = value;
    } else {
      target['id.ssn9'] = value;
      target['id.ssn4'] = value.slice(-4);
    }
  }
};
const buildVault = (entityAttributes: EntityAttribute[]): VaultType => {
  const vaultsAndTransforms: VaultType = {
    vault: {},
    transforms: {},
    dataKinds: {}
  };
  entityAttributes.forEach(attribute => {
    // @ts-expect-error: cannot type this properly
    vaultsAndTransforms.vault[attribute.identifier] = attribute.value;
    vaultsAndTransforms.transforms[attribute.identifier] = attribute.transforms;
    vaultsAndTransforms.dataKinds[attribute.identifier] = attribute.dataKind;
  });
  return vaultsAndTransforms;
};

// This is a custom hook that returns the vault for an entity.
// The vault is stored in the query cache, so can persist and access from anywhere (e.g list page)
// The main reason for keeping it separated is is because we should not overwrite it once we make an entity request,
// any only when we decrypt some data we should update it.
const useEntityVault = t0 => {
  const $ = _c(39);
  const {
    entityAttributes,
    entityId,
    seqno
  } = t0;
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== entityId || $[1] !== seqno) {
    t1 = getQueryKey(entityId, seqno);
    $[0] = entityId;
    $[1] = seqno;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const queryKey = t1;
  let t2;
  if ($[3] !== entityAttributes || $[4] !== queryClient || $[5] !== queryKey) {
    t2 = newData => {
      const prevData = queryClient.getQueryData(queryKey) ?? (entityAttributes && buildVault(entityAttributes));
      const newDataConverted = {} as Partial<Record<DataIdentifier, VaultValue>>;
      Object.keys(newData.vault).forEach(diKey => {
        const di = diKey as DataIdentifier;
        const value = stripMaskDelimiters(di, newData.vault[di]);
        newDataConverted[di] = value === null ? undefined : value;
        deriveSiblingDis(di, value, newDataConverted);
      });
      const newVault = {
        ...prevData?.vault,
        ...newDataConverted
      };
      const newTransforms = {
        ...prevData?.transforms,
        ...newData.transforms
      };
      const newDataKinds = {
        ...prevData?.dataKinds,
        ...newData.dataKinds
      };
      queryClient.setQueryData(queryKey, {
        vault: newVault,
        transforms: newTransforms,
        dataKinds: newDataKinds
      });
    };
    $[3] = entityAttributes;
    $[4] = queryClient;
    $[5] = queryKey;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const update = t2;
  let t3;
  if ($[7] !== entityAttributes) {
    t3 = data => {
      if (!entityAttributes || !data) {
        return false;
      }
      const relevantDis = ["id.first_name", "id.middle_name", "id.last_name", "id.email", "id.phone_number", "id.address_line1", "id.address_line2", "id.city", "id.state", "id.zip", "id.country", "id.dob", "id.ssn4", "id.ssn9"];
      const decryptableDIs = entityAttributes.filter(_temp).map(_temp2).filter(di_0 => relevantDis.some(relevantDi => di_0.startsWith(relevantDi)));
      return decryptableDIs.every(di_1 => isVaultDataDecrypted(data.vault[di_1]));
    };
    $[7] = entityAttributes;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const isIdDecrypted = t3;
  let t4;
  if ($[9] !== entityAttributes) {
    t4 = data_0 => {
      if (!entityAttributes || !data_0) {
        return false;
      }
      const decryptableDIs_0 = entityAttributes.filter(_temp3).map(_temp4);
      const nonDocumentDIs = decryptableDIs_0.filter(_temp5);
      return nonDocumentDIs.every(di_3 => {
        const value_0 = data_0.vault[di_3];
        return isVaultDataDecrypted(value_0);
      });
    };
    $[9] = entityAttributes;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const isAllNonDocumentDecrypted = t4;
  let t5;
  if ($[11] !== entityAttributes) {
    t5 = data_1 => {
      if (!entityAttributes || !data_1) {
        return false;
      }
      const decryptableDIs_1 = entityAttributes.filter(_temp6).map(_temp7);
      return decryptableDIs_1.every(di_4 => {
        const value_1 = data_1.vault[di_4];
        return isVaultDataDecrypted(value_1);
      });
    };
    $[11] = entityAttributes;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const isAllDecrypted = t5;
  let t6;
  if ($[13] !== entityAttributes) {
    t6 = di_5 => entityAttributes?.some(attr_5 => attr_5.isDecryptable && attr_5.identifier === di_5);
    $[13] = entityAttributes;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const isDecryptable = t6;
  const isDecrypted = _temp8;
  const hasDi = di_7 => query.data?.vault[di_7] !== undefined;
  let t7;
  if ($[15] !== entityAttributes || $[16] !== entityId || $[17] !== queryClient || $[18] !== queryKey || $[19] !== seqno) {
    t7 = () => {
      const cached = queryClient.getQueryData(queryKey);
      if (cached) {
        return cached;
      }
      const attributes = entityAttributes ?? [];
      if (entityId) {
        forgetDecryptedFields(entityId, attributes.map(t8 => {
          const {
            identifier
          } = t8;
          return (seqno === undefined ? identifier : `${identifier}:${seqno}`) as DataIdentifier;
        }));
      }
      return buildVault(attributes);
    };
    $[15] = entityAttributes;
    $[16] = entityId;
    $[17] = queryClient;
    $[18] = queryKey;
    $[19] = seqno;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== entityAttributes) {
    t8 = () => entityAttributes ? buildVault(entityAttributes) : undefined;
    $[21] = entityAttributes;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const t9 = !!entityId && !!entityAttributes;
  let t10;
  if ($[23] !== queryKey || $[24] !== t7 || $[25] !== t8 || $[26] !== t9) {
    t10 = {
      queryKey,
      queryFn: t7,
      placeholderData: t8,
      enabled: t9,
      staleTime: Number.POSITIVE_INFINITY
    };
    $[23] = queryKey;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const query = useQuery(t10);
  const t11 = query;
  const t12 = isAllDecrypted(query.data);
  const t13 = isIdDecrypted(query.data);
  const t14 = isAllNonDocumentDecrypted(query.data);
  let t15;
  if ($[28] !== query) {
    t15 = di_8 => isDecrypted(query.data, di_8);
    $[28] = query;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== hasDi || $[31] !== isDecryptable || $[32] !== t11 || $[33] !== t12 || $[34] !== t13 || $[35] !== t14 || $[36] !== t15 || $[37] !== update) {
    t16 = {
      ...t11,
      update,
      isAllDecrypted: t12,
      isIdDecrypted: t13,
      isAllNonDocumentDecrypted: t14,
      isDecryptable,
      isDecrypted: t15,
      hasDi
    };
    $[30] = hasDi;
    $[31] = isDecryptable;
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
    $[35] = t14;
    $[36] = t15;
    $[37] = update;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  return t16;
};
export default useEntityVault;
function _temp(attr) {
  return attr.isDecryptable;
}
function _temp2(attr_0) {
  return attr_0.identifier;
}
function _temp3(attr_1) {
  return attr_1.isDecryptable;
}
function _temp4(attr_2) {
  return attr_2.identifier;
}
function _temp5(di_2) {
  return !di_2.startsWith("document");
}
function _temp6(attr_3) {
  return attr_3.isDecryptable;
}
function _temp7(attr_4) {
  return attr_4.identifier;
}
function _temp8(data_2, di_6) {
  return isVaultDataDecrypted(data_2?.vault[di_6]);
}